import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { pilotAPI } from '../../lib/api';
import { 
  Buildings,
  ChartLineUp,
  Clock,
  Plus,
  Eye,
  Play,
  X,
  CheckCircle,
  Warning,
  CurrencyDollar,
  Users,
  Calendar,
  CaretRight,
  FileText,
  Spinner,
  ArrowUp,
  ArrowDown
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const TIER_INFO = {
  lite: { name: 'Lite', price: 4500, providers: '1-2', color: 'bg-slate-100 text-slate-700' },
  core: { name: 'Core', price: 7500, providers: '2-3', color: 'bg-teal-100 text-teal-700' },
  expanded: { name: 'Expanded', price: 12000, providers: 'Up to 5', color: 'bg-blue-100 text-blue-700' }
};

const STATUS_BADGES = {
  enrolled: { label: 'Enrolled', color: 'bg-amber-100 text-amber-700' },
  active: { label: 'Active', color: 'bg-green-100 text-green-700' },
  week_4: { label: 'Week 4 Review', color: 'bg-purple-100 text-purple-700' },
  completed: { label: 'Completed', color: 'bg-blue-100 text-blue-700' },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700' }
};

export default function PilotProgramPage() {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showMetricsModal, setShowMetricsModal] = useState(false);
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [clinicDetails, setClinicDetails] = useState(null);
  const [roiReport, setRoiReport] = useState(null);
  
  const [newClinic, setNewClinic] = useState({
    clinic_name: '',
    clinic_type: 'primary_care',
    address: '',
    city: '',
    state: '',
    phone: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    contact_role: 'clinic_admin',
    ehr_system: 'epic',
    tier: 'core',
    provider_count: 2,
    provider_names: [],
    notes: ''
  });
  
  const [newMetrics, setNewMetrics] = useState({
    week_number: 1,
    period_start: '',
    period_end: '',
    total_visits: 0,
    telehealth_visits: 0,
    avg_provider_time_per_visit: 0,
    avg_documentation_time: 0,
    visits_per_provider_per_day: 0,
    intake_completion_rate: 0,
    cma_assisted_visits: 0,
    provider_satisfaction_score: 0,
    data_source: 'manual'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [dashRes, clinicsRes] = await Promise.all([
        pilotAPI.getDashboard(),
        pilotAPI.getClinics()
      ]);
      setDashboard(dashRes.data);
      setClinics(clinicsRes.data.clinics || []);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error loading pilot data:', error);
      toast.error('Failed to load pilot data');
    } finally {
      setLoading(false);
    }
  };

  const handleEnrollClinic = async (e) => {
    e.preventDefault();
    try {
      await pilotAPI.enrollClinic(newClinic);
      toast.success('Clinic enrolled successfully!');
      setShowEnrollModal(false);
      setNewClinic({
        clinic_name: '',
        clinic_type: 'primary_care',
        address: '',
        city: '',
        state: '',
        phone: '',
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        contact_role: 'clinic_admin',
        ehr_system: 'epic',
        tier: 'core',
        provider_count: 2,
        provider_names: [],
        notes: ''
      });
      loadData();
    } catch (error) {
      toast.error('Failed to enroll clinic');
    }
  };

  const handleStartPilot = async (clinicId) => {
    try {
      await pilotAPI.startPilot(clinicId);
      toast.success('Pilot program started!');
      loadData();
    } catch (error) {
      toast.error('Failed to start pilot');
    }
  };

  const handleViewDetails = async (clinic) => {
    setSelectedClinic(clinic);
    try {
      const [detailsRes, roiRes] = await Promise.all([
        pilotAPI.getClinic(clinic.id),
        pilotAPI.getROIReport(clinic.id).catch(() => null)
      ]);
      setClinicDetails(detailsRes.data);
      setRoiReport(roiRes?.data || null);
      setShowDetailModal(true);
    } catch (error) {
      toast.error('Failed to load clinic details');
    }
  };

  const handleRecordMetrics = async (e) => {
    e.preventDefault();
    try {
      await pilotAPI.recordMetrics({
        clinic_id: selectedClinic.id,
        ...newMetrics
      });
      toast.success('Metrics recorded!');
      setShowMetricsModal(false);
      handleViewDetails(selectedClinic);
    } catch (error) {
      toast.error('Failed to record metrics');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner size={32} className="animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
            8-Week Pilot Program
          </h1>
          <p className="text-slate-500 mt-1">Manage clinic pilots and track ROI metrics</p>
        </div>
        <button
          onClick={() => setShowEnrollModal(true)}
          className="btn-teal flex items-center gap-2"
          data-testid="enroll-clinic-btn"
        >
          <Plus size={20} />
          Enroll New Clinic
        </button>
      </div>

      {/* Dashboard Overview */}
      {dashboard && (
        <div className="grid grid-cols-4 gap-4 mb-8">
          <div className="card-clinical p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                <Buildings size={20} className="text-teal-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{dashboard.overview.total_enrolled}</p>
                <p className="text-sm text-slate-500">Total Clinics</p>
              </div>
            </div>
          </div>
          
          <div className="card-clinical p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Play size={20} className="text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{dashboard.overview.active_pilots}</p>
                <p className="text-sm text-slate-500">Active Pilots</p>
              </div>
            </div>
          </div>
          
          <div className="card-clinical p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <CheckCircle size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{dashboard.overview.completed}</p>
                <p className="text-sm text-slate-500">Completed</p>
              </div>
            </div>
          </div>
          
          <div className="card-clinical p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <CurrencyDollar size={20} className="text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">${dashboard.revenue.collected.toLocaleString()}</p>
                <p className="text-sm text-slate-500">Revenue Collected</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Active Pilots */}
      {dashboard?.active_clinics?.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Clock size={20} className="text-teal-600" />
            Active Pilots
          </h2>
          <div className="grid grid-cols-3 gap-4">
            {dashboard.active_clinics.map((clinic) => (
              <div key={clinic.id} className="card-clinical p-4 border-l-4 border-l-teal-500">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-semibold text-slate-800">{clinic.name}</h3>
                  <span className={`text-xs px-2 py-1 rounded-full ${TIER_INFO[clinic.tier]?.color}`}>
                    {TIER_INFO[clinic.tier]?.name}
                  </span>
                </div>
                <div className="space-y-1 text-sm text-slate-600">
                  <p className="flex items-center gap-2">
                    <Calendar size={14} />
                    Week {clinic.current_week} of 8
                  </p>
                  <p className="flex items-center gap-2">
                    <Users size={14} />
                    {clinic.provider_count} providers
                  </p>
                  <p className="flex items-center gap-2">
                    <Clock size={14} />
                    {clinic.days_remaining} days remaining
                  </p>
                </div>
                <div className="mt-3 w-full bg-slate-200 rounded-full h-2">
                  <div 
                    className="bg-teal-500 h-2 rounded-full transition-all"
                    style={{ width: `${(clinic.current_week / 8) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All Clinics Table */}
      <div className="card-clinical overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <h2 className="font-semibold text-slate-800">All Enrolled Clinics</h2>
        </div>
        <table className="w-full" data-testid="clinics-table">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left p-4 text-sm font-medium text-slate-600">Clinic</th>
              <th className="text-left p-4 text-sm font-medium text-slate-600">Tier</th>
              <th className="text-left p-4 text-sm font-medium text-slate-600">Status</th>
              <th className="text-left p-4 text-sm font-medium text-slate-600">Providers</th>
              <th className="text-left p-4 text-sm font-medium text-slate-600">Payment</th>
              <th className="text-left p-4 text-sm font-medium text-slate-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {clinics.length === 0 ? (
              <tr>
                <td colSpan="6" className="p-8 text-center text-slate-500">
                  No clinics enrolled yet. Click &quot;Enroll New Clinic&quot; to get started.
                </td>
              </tr>
            ) : (
              clinics.map((clinic) => (
                <tr key={clinic.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="p-4">
                    <div>
                      <p className="font-medium text-slate-800">{clinic.clinic_name}</p>
                      <p className="text-sm text-slate-500">{clinic.contact_name}</p>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className={`text-xs px-2 py-1 rounded-full ${TIER_INFO[clinic.tier]?.color}`}>
                      {TIER_INFO[clinic.tier]?.name} - ${TIER_INFO[clinic.tier]?.price.toLocaleString()}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className={`text-xs px-2 py-1 rounded-full ${STATUS_BADGES[clinic.status]?.color}`}>
                      {STATUS_BADGES[clinic.status]?.label}
                    </span>
                    {clinic.current_week && (
                      <span className="ml-2 text-xs text-slate-500">Week {clinic.current_week}</span>
                    )}
                  </td>
                  <td className="p-4 text-slate-600">{clinic.provider_count}</td>
                  <td className="p-4">
                    <span className={`text-sm ${clinic.payment_status === 'paid' ? 'text-green-600' : 'text-amber-600'}`}>
                      ${clinic.amount_paid?.toLocaleString() || 0} / ${clinic.payment_amount?.toLocaleString()}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleViewDetails(clinic)}
                        className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg"
                        title="View Details"
                      >
                        <Eye size={18} />
                      </button>
                      {clinic.status === 'enrolled' && (
                        <button
                          onClick={() => handleStartPilot(clinic.id)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                          title="Start Pilot"
                        >
                          <Play size={18} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Enroll Modal */}
      {showEnrollModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center">
              <h2 className="text-xl font-bold text-slate-900">Enroll New Clinic</h2>
              <button onClick={() => setShowEnrollModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleEnrollClinic} className="p-6 space-y-6">
              {/* Clinic Info */}
              <div>
                <h3 className="font-semibold text-slate-800 mb-3">Clinic Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="label-clinical">Clinic Name *</label>
                    <input
                      type="text"
                      value={newClinic.clinic_name}
                      onChange={(e) => setNewClinic({...newClinic, clinic_name: e.target.value})}
                      className="input-clinical"
                      required
                    />
                  </div>
                  <div>
                    <label className="label-clinical">Clinic Type</label>
                    <select
                      value={newClinic.clinic_type}
                      onChange={(e) => setNewClinic({...newClinic, clinic_type: e.target.value})}
                      className="input-clinical"
                    >
                      <option value="primary_care">Primary Care</option>
                      <option value="urgent_care">Urgent Care</option>
                      <option value="telehealth">Telehealth Practice</option>
                      <option value="specialty">Specialty Clinic</option>
                    </select>
                  </div>
                  <div>
                    <label className="label-clinical">EHR System</label>
                    <select
                      value={newClinic.ehr_system}
                      onChange={(e) => setNewClinic({...newClinic, ehr_system: e.target.value})}
                      className="input-clinical"
                    >
                      <option value="epic">Epic</option>
                      <option value="eclinicalworks">eClinicalWorks</option>
                      <option value="athena">Athenahealth</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="col-span-2">
                    <label className="label-clinical">Address</label>
                    <input
                      type="text"
                      value={newClinic.address}
                      onChange={(e) => setNewClinic({...newClinic, address: e.target.value})}
                      className="input-clinical"
                    />
                  </div>
                  <div>
                    <label className="label-clinical">City</label>
                    <input
                      type="text"
                      value={newClinic.city}
                      onChange={(e) => setNewClinic({...newClinic, city: e.target.value})}
                      className="input-clinical"
                    />
                  </div>
                  <div>
                    <label className="label-clinical">State</label>
                    <input
                      type="text"
                      value={newClinic.state}
                      onChange={(e) => setNewClinic({...newClinic, state: e.target.value})}
                      className="input-clinical"
                      maxLength={2}
                    />
                  </div>
                </div>
              </div>
              
              {/* Contact Info */}
              <div>
                <h3 className="font-semibold text-slate-800 mb-3">Contact Person</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label-clinical">Contact Name *</label>
                    <input
                      type="text"
                      value={newClinic.contact_name}
                      onChange={(e) => setNewClinic({...newClinic, contact_name: e.target.value})}
                      className="input-clinical"
                      required
                    />
                  </div>
                  <div>
                    <label className="label-clinical">Contact Email *</label>
                    <input
                      type="email"
                      value={newClinic.contact_email}
                      onChange={(e) => setNewClinic({...newClinic, contact_email: e.target.value})}
                      className="input-clinical"
                      required
                    />
                  </div>
                  <div>
                    <label className="label-clinical">Contact Phone</label>
                    <input
                      type="tel"
                      value={newClinic.contact_phone}
                      onChange={(e) => setNewClinic({...newClinic, contact_phone: e.target.value})}
                      className="input-clinical"
                    />
                  </div>
                  <div>
                    <label className="label-clinical">Role</label>
                    <select
                      value={newClinic.contact_role}
                      onChange={(e) => setNewClinic({...newClinic, contact_role: e.target.value})}
                      className="input-clinical"
                    >
                      <option value="clinic_admin">Clinic Administrator</option>
                      <option value="office_manager">Office Manager</option>
                      <option value="provider">Provider</option>
                    </select>
                  </div>
                </div>
              </div>
              
              {/* Pilot Tier */}
              <div>
                <h3 className="font-semibold text-slate-800 mb-3">Pilot Tier</h3>
                <div className="grid grid-cols-3 gap-4">
                  {Object.entries(TIER_INFO).map(([key, info]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setNewClinic({...newClinic, tier: key, provider_count: key === 'lite' ? 2 : key === 'core' ? 3 : 5})}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        newClinic.tier === key 
                          ? 'border-teal-500 bg-teal-50' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <p className="font-semibold text-slate-800">{info.name}</p>
                      <p className="text-2xl font-bold text-teal-600">${info.price.toLocaleString()}</p>
                      <p className="text-sm text-slate-500">{info.providers} providers</p>
                      {key === 'core' && (
                        <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full mt-2 inline-block">
                          Most Popular
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="label-clinical">Number of Providers</label>
                <input
                  type="number"
                  value={newClinic.provider_count}
                  onChange={(e) => setNewClinic({...newClinic, provider_count: parseInt(e.target.value) || 1})}
                  className="input-clinical"
                  min="1"
                  max={newClinic.tier === 'lite' ? 2 : newClinic.tier === 'core' ? 3 : 5}
                />
              </div>
              
              <div>
                <label className="label-clinical">Notes</label>
                <textarea
                  value={newClinic.notes}
                  onChange={(e) => setNewClinic({...newClinic, notes: e.target.value})}
                  className="input-clinical min-h-[80px]"
                  placeholder="Any additional notes about this clinic..."
                />
              </div>
              
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button type="button" onClick={() => setShowEnrollModal(false)} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn-teal">
                  Enroll Clinic
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && clinicDetails && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-slate-900">{clinicDetails.clinic.clinic_name}</h2>
                <p className="text-slate-500">{clinicDetails.clinic.contact_email}</p>
              </div>
              <div className="flex items-center gap-3">
                {clinicDetails.clinic.status === 'active' && (
                  <button
                    onClick={() => {
                      setShowMetricsModal(true);
                      setNewMetrics({
                        ...newMetrics,
                        week_number: (clinicDetails.metrics?.length || 0) + 1
                      });
                    }}
                    className="btn-teal flex items-center gap-2"
                  >
                    <ChartLineUp size={18} />
                    Record Metrics
                  </button>
                )}
                <button onClick={() => setShowDetailModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                  <X size={20} />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Quick Stats */}
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-sm text-slate-500">Status</p>
                  <span className={`text-sm px-2 py-1 rounded-full ${STATUS_BADGES[clinicDetails.clinic.status]?.color}`}>
                    {STATUS_BADGES[clinicDetails.clinic.status]?.label}
                  </span>
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-sm text-slate-500">Tier</p>
                  <p className="font-semibold">{TIER_INFO[clinicDetails.clinic.tier]?.name}</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-sm text-slate-500">Weeks Completed</p>
                  <p className="font-semibold">{clinicDetails.metrics?.length || 0} / 8</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-sm text-slate-500">Providers</p>
                  <p className="font-semibold">{clinicDetails.clinic.provider_count}</p>
                </div>
              </div>
              
              {/* ROI Report Summary */}
              {roiReport && roiReport.key_metrics && Object.keys(roiReport.key_metrics).length > 0 && (
                <div>
                  <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                    <ChartLineUp size={20} className="text-teal-600" />
                    ROI Summary
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    {roiReport.key_metrics.provider_time_reduction && (
                      <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                        <p className="text-sm text-green-700 mb-1">Provider Time Reduction</p>
                        <p className="text-2xl font-bold text-green-600 flex items-center gap-1">
                          <ArrowDown size={20} />
                          {roiReport.key_metrics.provider_time_reduction.improvement}
                        </p>
                        <p className="text-xs text-green-600">Goal: 25-40%</p>
                      </div>
                    )}
                    {roiReport.key_metrics.visit_capacity_increase && (
                      <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                        <p className="text-sm text-blue-700 mb-1">Visit Capacity Increase</p>
                        <p className="text-2xl font-bold text-blue-600 flex items-center gap-1">
                          <ArrowUp size={20} />
                          {roiReport.key_metrics.visit_capacity_increase.improvement}
                        </p>
                        <p className="text-xs text-blue-600">Goal: 20-30%</p>
                      </div>
                    )}
                    {roiReport.key_metrics.intake_completion_rate && (
                      <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
                        <p className="text-sm text-purple-700 mb-1">Intake Completion</p>
                        <p className="text-2xl font-bold text-purple-600">
                          {roiReport.key_metrics.intake_completion_rate.current}
                        </p>
                        <p className="text-xs text-purple-600">Goal: 90%+</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Weekly Metrics */}
              {clinicDetails.metrics && clinicDetails.metrics.length > 0 && (
                <div>
                  <h3 className="font-semibold text-slate-800 mb-3">Weekly Metrics</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left p-3">Week</th>
                          <th className="text-left p-3">Total Visits</th>
                          <th className="text-left p-3">Avg Provider Time</th>
                          <th className="text-left p-3">Visits/Day</th>
                          <th className="text-left p-3">Intake Rate</th>
                          <th className="text-left p-3">CMA Assisted</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clinicDetails.metrics.map((m) => (
                          <tr key={m.id} className="border-t">
                            <td className="p-3 font-medium">Week {m.week_number}</td>
                            <td className="p-3">{m.total_visits}</td>
                            <td className="p-3">{m.avg_provider_time_per_visit} min</td>
                            <td className="p-3">{m.visits_per_provider_per_day}</td>
                            <td className="p-3">{m.intake_completion_rate}%</td>
                            <td className="p-3">{m.cma_assisted_visits}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Metrics Entry Modal */}
      {showMetricsModal && selectedClinic && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center">
              <h2 className="text-xl font-bold text-slate-900">Record Weekly Metrics</h2>
              <button onClick={() => setShowMetricsModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleRecordMetrics} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-clinical">Week Number</label>
                  <input
                    type="number"
                    value={newMetrics.week_number}
                    onChange={(e) => setNewMetrics({...newMetrics, week_number: parseInt(e.target.value)})}
                    className="input-clinical"
                    min="1"
                    max="8"
                  />
                </div>
                <div>
                  <label className="label-clinical">Data Source</label>
                  <select
                    value={newMetrics.data_source}
                    onChange={(e) => setNewMetrics({...newMetrics, data_source: e.target.value})}
                    className="input-clinical"
                  >
                    <option value="manual">Manual Entry</option>
                    <option value="automatic">Automatic (T-MedCMA)</option>
                    <option value="mixed">Mixed</option>
                  </select>
                </div>
                <div>
                  <label className="label-clinical">Period Start</label>
                  <input
                    type="date"
                    value={newMetrics.period_start}
                    onChange={(e) => setNewMetrics({...newMetrics, period_start: e.target.value})}
                    className="input-clinical"
                  />
                </div>
                <div>
                  <label className="label-clinical">Period End</label>
                  <input
                    type="date"
                    value={newMetrics.period_end}
                    onChange={(e) => setNewMetrics({...newMetrics, period_end: e.target.value})}
                    className="input-clinical"
                  />
                </div>
              </div>
              
              <hr className="my-4" />
              
              <h4 className="font-medium text-slate-700">Visit Metrics</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-clinical">Total Visits</label>
                  <input
                    type="number"
                    value={newMetrics.total_visits}
                    onChange={(e) => setNewMetrics({...newMetrics, total_visits: parseInt(e.target.value) || 0})}
                    className="input-clinical"
                  />
                </div>
                <div>
                  <label className="label-clinical">Telehealth Visits</label>
                  <input
                    type="number"
                    value={newMetrics.telehealth_visits}
                    onChange={(e) => setNewMetrics({...newMetrics, telehealth_visits: parseInt(e.target.value) || 0})}
                    className="input-clinical"
                  />
                </div>
                <div>
                  <label className="label-clinical">CMA Assisted Visits</label>
                  <input
                    type="number"
                    value={newMetrics.cma_assisted_visits}
                    onChange={(e) => setNewMetrics({...newMetrics, cma_assisted_visits: parseInt(e.target.value) || 0})}
                    className="input-clinical"
                  />
                </div>
                <div>
                  <label className="label-clinical">Visits per Provider/Day</label>
                  <input
                    type="number"
                    step="0.1"
                    value={newMetrics.visits_per_provider_per_day}
                    onChange={(e) => setNewMetrics({...newMetrics, visits_per_provider_per_day: parseFloat(e.target.value) || 0})}
                    className="input-clinical"
                  />
                </div>
              </div>
              
              <hr className="my-4" />
              
              <h4 className="font-medium text-slate-700">Time Metrics (minutes)</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-clinical">Avg Provider Time/Visit</label>
                  <input
                    type="number"
                    step="0.1"
                    value={newMetrics.avg_provider_time_per_visit}
                    onChange={(e) => setNewMetrics({...newMetrics, avg_provider_time_per_visit: parseFloat(e.target.value) || 0})}
                    className="input-clinical"
                  />
                </div>
                <div>
                  <label className="label-clinical">Avg Documentation Time</label>
                  <input
                    type="number"
                    step="0.1"
                    value={newMetrics.avg_documentation_time}
                    onChange={(e) => setNewMetrics({...newMetrics, avg_documentation_time: parseFloat(e.target.value) || 0})}
                    className="input-clinical"
                  />
                </div>
              </div>
              
              <hr className="my-4" />
              
              <h4 className="font-medium text-slate-700">Quality Metrics</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-clinical">Intake Completion Rate (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={newMetrics.intake_completion_rate}
                    onChange={(e) => setNewMetrics({...newMetrics, intake_completion_rate: parseFloat(e.target.value) || 0})}
                    className="input-clinical"
                    max="100"
                  />
                </div>
                <div>
                  <label className="label-clinical">Provider Satisfaction (1-5)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={newMetrics.provider_satisfaction_score}
                    onChange={(e) => setNewMetrics({...newMetrics, provider_satisfaction_score: parseFloat(e.target.value) || 0})}
                    className="input-clinical"
                    min="1"
                    max="5"
                  />
                </div>
              </div>
              
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button type="button" onClick={() => setShowMetricsModal(false)} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn-teal">
                  Save Metrics
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
